// store/slices/turnaroundsSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import TurnAroundService from '@/services/turnarounds';
import { Turnaround, TurnAroundRow } from '@/types/turnaround';

interface TurnaroundsState {
  data: TurnAroundRow[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  fetched: boolean;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
}

const initialState: TurnaroundsState = {
  data: [],
  loading: false,
  error: null,
  totalCount: 0,
  fetched: false,
  status: 'idle',
};

// Async thunk for fetching turnarounds
export const fetchTurnarounds = createAsyncThunk<
  { results: TurnAroundRow[]; totalCount: number },
  { userId: string; siteId: string; accessToken?: string }
>(
  'turnarounds/fetchTurnarounds',
  async ({ userId, siteId, accessToken }) => {
    const turnAroundService = new TurnAroundService(undefined, accessToken);
    const response = await turnAroundService.getTurnarounds();
    
    const mappedRows = response.results.map((row: Turnaround) => ({
      id: row.turnaround_id,
      name: row.name,
      status: row.status,
      start_date: row.start_date,
      end_date: row.end_date,
      devices: row.device_count,
      location: row.location_name,
      location_id: row.location_id,
      site: row.site_name,
      notes: '',
    }));
    
    return {
      results: mappedRows,
      totalCount: response.totalCount,
    };
  }
);

const turnaroundsSlice = createSlice({
  name: 'turnarounds',
  initialState,
  reducers: {
    clearTurnarounds: (state) => {
      state.data = [];
      state.error = null;
      state.totalCount = 0;
      state.fetched = false;
      state.status = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTurnarounds.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.fetched = false;
        state.status = 'loading';
      })
      .addCase(fetchTurnarounds.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.results;
        state.totalCount = action.payload.totalCount;
        state.error = null;
        state.fetched = true;
        state.status = 'succeeded';
      })
      .addCase(fetchTurnarounds.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load turnarounds';
        state.data = [];
        state.fetched = true;
        state.status = 'failed';
      });
  },
});

export const { clearTurnarounds } = turnaroundsSlice.actions;
export default turnaroundsSlice.reducer;
